import { useState } from "react";
import { AUDIT_ACTIONS, AUDIT_ACTION_GROUPS } from "@shared/schemas/admin";
import { EmptyState } from "@client/components/EmptyState";
import { SelectField, TextField } from "@client/components/Field";
import { ErrorNotice, LoadingBlock } from "@client/components/Notices";
import { Panel } from "@client/components/Panel";
import { Button } from "@client/components/Button";
import { useAdminUsers, useAuditLog } from "@client/hooks/useAdmin";
import { formatDateTime } from "@client/lib/datetime";

const PAGE_SIZE = 100;

/**
 * "allowlist.revoke" → "Revoke". The group already carries the noun, so repeating
 * it in every option turns a scannable list into a column of prefixes.
 */
function actionLabel(action: string): string {
  const suffix = action.slice(action.indexOf(".") + 1).replace(/\./g, " ");
  return suffix.charAt(0).toUpperCase() + suffix.slice(1);
}

/**
 * `/admin/audit` — what happened, who did it, and when.
 *
 * The action filter is generated from the same closed list the writers are typed
 * against (`AUDIT_ACTIONS`), so it cannot quietly omit an action and hide rows
 * that do exist. Rows themselves are never filtered client-side for the same
 * reason the products list is not: one answer to what is in the log.
 */
export function AdminAuditPage() {
  const [action, setAction] = useState("");
  const [actorId, setActorId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(0);

  const filters = {
    action: action || undefined,
    actorId: actorId || undefined,
    from: from || undefined,
    to: to || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };
  const audit = useAuditLog(filters);
  // The actor filter needs names, and the same list the users screen shows is
  // exactly the right one — cached by TanStack Query, so it costs nothing here.
  const users = useAdminUsers({});

  const items = audit.data?.items ?? [];
  const total = audit.data?.total ?? 0;
  const filtered = Boolean(action || actorId || from || to);

  function change(setter: (value: string) => void) {
    return (value: string) => {
      setter(value);
      // A filter change that kept the offset would land on page four of a
      // two-page result and look like an empty log.
      setPage(0);
    };
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-bold text-ink">Audit log</h1>
        <p className="max-w-prose text-sm text-ink-soft">
          Every sign-in, allow-list change and scenario write, newest first. Entries are never
          edited or removed, and a refused sign-in is recorded with the address that was refused
          even though no account exists for it.
        </p>
      </header>

      <Panel>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <SelectField
            label="Action"
            value={action}
            onChange={(event) => change(setAction)(event.target.value)}
          >
            <option value="">Any action</option>
            {AUDIT_ACTION_GROUPS.map((group) => (
              <optgroup key={group.prefix} label={group.label}>
                {AUDIT_ACTIONS.filter((candidate) => candidate.startsWith(group.prefix)).map(
                  (candidate) => (
                    <option key={candidate} value={candidate}>
                      {actionLabel(candidate)}
                    </option>
                  ),
                )}
              </optgroup>
            ))}
          </SelectField>

          <SelectField
            label="Who"
            value={actorId}
            hint={users.isError ? "The list of people could not be loaded." : undefined}
            onChange={(event) => change(setActorId)(event.target.value)}
          >
            <option value="">Anyone</option>
            {(users.data?.items ?? []).map((user) => (
              <option key={user.id} value={user.id}>
                {user.fullName}
              </option>
            ))}
          </SelectField>

          <TextField
            label="From"
            type="date"
            value={from}
            max={to || undefined}
            onChange={(event) => change(setFrom)(event.target.value)}
          />
          <TextField
            label="To"
            type="date"
            value={to}
            min={from || undefined}
            hint="Includes everything that happened on this day."
            onChange={(event) => change(setTo)(event.target.value)}
          />
        </div>

        {filtered && (
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setAction("");
                setActorId("");
                setFrom("");
                setTo("");
                setPage(0);
              }}
            >
              Clear filters
            </Button>
          </div>
        )}

        {audit.isPending && <LoadingBlock rows={6} label="Loading the audit log" />}

        {audit.isError && <ErrorNotice error={audit.error} onRetry={() => void audit.refetch()} />}

        {audit.isSuccess && items.length === 0 && (
          <EmptyState
            title={filtered ? "Nothing matches those filters" : "The log is empty"}
            description={
              filtered
                ? "No entry falls inside that combination of action, person and dates."
                : "Nothing has been recorded yet. The first sign-in will appear here."
            }
          />
        )}

        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[52rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-line text-left text-[11px] uppercase tracking-wider text-ink-soft">
                  <th scope="col" className="px-3 py-2 font-semibold">
                    When
                  </th>
                  <th scope="col" className="px-3 py-2 font-semibold">
                    Who
                  </th>
                  <th scope="col" className="px-3 py-2 font-semibold">
                    Action
                  </th>
                  <th scope="col" className="px-3 py-2 font-semibold">
                    What happened
                  </th>
                  <th scope="col" className="px-3 py-2 font-semibold">
                    From
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} className="border-b border-line align-top">
                    <td className="px-3 py-2 text-xs text-ink-soft">
                      {formatDateTime(row.createdAt)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-semibold text-ink">
                          {row.actor?.fullName ?? row.actorEmail}
                        </span>
                        {row.actor && (
                          <span className="text-[11px] text-ink-soft">{row.actorEmail}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <code className="rounded bg-chip-bg px-1.5 py-0.5 text-[11px] text-ink-soft">
                        {row.action}
                      </code>
                    </td>
                    <td className="px-3 py-2 text-xs text-ink">
                      <div className="flex flex-col gap-1">
                        <span>{row.summary ?? "—"}</span>
                        {row.metadata != null && (
                          <details className="text-[11px] text-ink-soft">
                            <summary className="cursor-pointer font-semibold">Details</summary>
                            <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words rounded bg-chip-bg p-2">
                              {JSON.stringify(row.metadata, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-[11px] text-ink-soft">{row.ip ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-ink-soft">
              {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + items.length} of {total}
            </span>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((current) => Math.max(0, current - 1))}
              >
                Newer
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage((current) => current + 1)}
              >
                Older
              </Button>
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
